/**
 * Lifted filter-values state for an exception page.
 *
 * Originally `DataViewTable` owned `filterValues` (the per-filter map
 * driving multiselect / select / toggle / search predicates) plus the
 * `filterEnabled` boolean for the optional filter-toggle switch. Once the
 * unified `DataViewFilterPanel` started rendering those controls inside
 * the filter card, both the panel and the table needed access to the
 * same state — so it moves up to the route via this hook.
 *
 * Returned helpers:
 * - `filterValues` / `updateFilterValue` — per-filter value map + setter
 * - `filterEnabled` / `setFilterEnabled` — boolean for `config.filterToggle`
 * - `resetFilters()` — back to the initial map + toggle default
 * - `hasAnyActiveFilter` — drives the panel's Reset action disabled state
 *
 * The hook resets filter values when `config.filters` changes (e.g. the
 * route swaps to a different exception code with a different filters
 * list). It does NOT touch URL state — that's the route's job in the
 * full reset flow.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  buildInitialFilterValues,
} from '../components/inspector/shared/exceptionFilterControls'
import type {
  ExceptionConfig,
  ExceptionFilterDef,
} from '../config/exceptions/types'

export interface UseExceptionFilterValuesResult {
  filterValues: Record<string, any>
  updateFilterValue: (key: string, value: any) => void
  setFilterValues: (next: Record<string, any>) => void
  filterEnabled: boolean
  setFilterEnabled: (next: boolean) => void
  resetFilters: () => void
  hasAnyActiveFilter: boolean
}

export const useExceptionFilterValues = (
  config: ExceptionConfig | null,
): UseExceptionFilterValuesResult => {
  const filters: ExceptionFilterDef[] | undefined = config?.filters

  const [filterValues, setFilterValues] = useState<Record<string, any>>(
    () => buildInitialFilterValues(filters),
  )
  const [filterEnabled, setFilterEnabled] = useState<boolean>(
    () => !!config?.filterToggle?.defaultEnabled,
  )

  // Reset to defaults when the underlying filters list changes (i.e. the
  // route mounts a different exception code). Comparing by reference is
  // sufficient — configs are static module-level objects.
  useEffect(() => {
    setFilterValues(buildInitialFilterValues(filters))
    setFilterEnabled(!!config?.filterToggle?.defaultEnabled)
  }, [filters, config?.filterToggle?.defaultEnabled])

  const updateFilterValue = useCallback((key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const resetFilters = useCallback(() => {
    setFilterValues(buildInitialFilterValues(filters))
    setFilterEnabled(!!config?.filterToggle?.defaultEnabled)
  }, [filters, config?.filterToggle?.defaultEnabled])

  const hasAnyActiveFilter = useMemo(() => {
    for (const f of filters ?? []) {
      const v = filterValues[f.key]
      if (f.type === 'multiselect') {
        if (Array.isArray(v) && v.length > 0) return true
      } else if (f.type === 'select') {
        const defaultV = f.defaultValue ?? 'all'
        if (v && v !== defaultV) return true
      } else if (f.type === 'toggle') {
        if (!!v !== !!f.defaultEnabled) return true
      } else if (f.type === 'search') {
        if (typeof v === 'string' && v.trim() !== '') return true
      } else if (f.type === 'daterange') {
        if (Array.isArray(v) && (v[0] || v[1])) return true
      }
    }
    if (config?.filterToggle && filterEnabled !== !!config.filterToggle.defaultEnabled) {
      return true
    }
    return false
  }, [filters, filterValues, filterEnabled, config?.filterToggle])

  return {
    filterValues,
    updateFilterValue,
    setFilterValues,
    filterEnabled,
    setFilterEnabled,
    resetFilters,
    hasAnyActiveFilter,
  }
}
